import {
  HeaderDescription,
  HeaderTitle,
} from "components/timesheet/review/header";
import {
  TimesheetContent,
  TimesheetEntries,
  TimesheetLoading,
  TimesheetRoot,
} from "components/timesheet/timesheet";

import EntryCard from "components/timesheet/card/entry";
import ReviewAlert from "./review-alert";
import { ReviewByDayLoader } from "./loader";
import { ReviewStatus } from "components/timesheet/review";
import { TIMESHEET_MYPROJECTS_REVIEW } from "@/lib/utils";
import { TimesheetReviewProvider } from "components/timesheet/context/review";
import { cn } from "utils";
import { toast } from "sonner";
import { useState } from "react";
import { useTimesheet } from "components/timesheet/context";
import { useUpdateTimesheetEntry } from "components/timesheet/review/hooks/useUpdateTimesheetEntry";

function TimesheetReviewContent() {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const { refresh } = useTimesheet(); // Get refresh from context

  const updateTimesheetMutation = useUpdateTimesheetEntry({
    onSuccess: () => {
      toast.success("Timesheet reviewed successfully");
      refresh();
    },
    onError: (error) => {
      // Handle specific error cases
      if (error.response?.status === 403) {
        toast.error(
          "Entry Not found, It may have been updated or removed recently"
        );
        window.location.reload();
      } else {
        toast.error("Failed to review timesheet");
      }
      console.error("Error updating timesheet:", error);
      throw error;
    },
  });

  async function handleUpdateTimesheet() {
    try {
      const item = {
        date: selectedEntry.date,
        approved_hours: selectedEntry.logged_hours,
        unapproved_hours: 0,
      };
      await updateTimesheetMutation.mutateAsync({
        id: selectedEntry.id,
        values: item,
      });

      setShowConfirmDialog(false);
    } catch (error) {
      // Error handling is now done in the mutation
      throw error;
    }
  }

  return (
    <>
      <TimesheetLoading>
        <ReviewByDayLoader />
      </TimesheetLoading>
      <TimesheetRoot>
        <div className="lg:pt-6 pt-4 ">
          <div className="flex flex-col gap-6 lg:px-6 px-4">
            <div className="flex  items-start md:gap-6 gap-4 md:flex-row flex-col">
              <div>
                <div className="flex">
                  <HeaderTitle>
                    {({ day, areLogsReviewed }) => (
                      <h1
                        className={cn(
                          "text-xl font-semibold mb-1",
                          areLogsReviewed && "text-muted-foreground"
                        )}
                      >
                        {day}
                      </h1>
                    )}
                  </HeaderTitle>
                  <ReviewStatus className="text-gray-500 size-4.5 ml-2" />
                </div>
                <HeaderDescription className="text-sm text-muted-foreground" />
              </div>
              <div className={"flex gap-3 md:flex-row flex-col-reverse"}>
                <div className="flex gap-6 justify-end items-center flex-row-reverse md:flex-row"></div>
              </div>
            </div>
          </div>
          <TimesheetContent
            hideIfNoEntry={true}
            className="mt-4 pl-4 lg:pl-6 flex gap-4.5 overflow-x-auto no-scrollbar py-1 "
          >
            <TimesheetEntries>
              {({ entry }) => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  href={`/${TIMESHEET_MYPROJECTS_REVIEW}/${entry.id}`}
                  isReview={true}
                  handleReviewClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedEntry(entry);
                    setShowConfirmDialog(true);
                  }}
                />
              )}
            </TimesheetEntries>
          </TimesheetContent>
        </div>
      </TimesheetRoot>
      <ReviewAlert
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={handleUpdateTimesheet}
        isLoading={updateTimesheetMutation.isPending}
      />
    </>
  );
}

export default function TimesheetReviewByDay({ date, filters }) {
  const defaultFilters = {
    date: { _eq: date },
  };

  return (
    <TimesheetReviewProvider
      date={date}
      filters={{ ...filters, ...defaultFilters }}
      key={JSON.stringify(filters)}
    >
      <TimesheetReviewContent />
    </TimesheetReviewProvider>
  );
}
